import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { PhantomWalletAdapter, SolflareWalletAdapter } from '@solana/wallet-adapter-wallets';
import { clusterApiUrl } from '@solana/web3.js';
import { AuthProvider } from './contexts/AuthContext';
import MainPage from './components/MainPage';
import TokenDetailsPage from './components/TokenDetailsPage';
import { EVM_CHAIN_IDS } from './utils/chains';
import NetworkDetailsPage from './components/NetworkDetailsPage';
import WalletDetailsPage from './components/WalletDetailsPage';
import FigurePage from './components/FigurePage';
import FiguresIndexPage from './components/FiguresIndexPage';
import CheckoutReturnPage from './components/CheckoutReturnPage';
import AutoUpdater from './components/AutoUpdater';
import ApiKeysPage from './components/ApiKeysPage';
import { AgentLabListPage, AgentLabAgentPage } from './components/AgentLabPage';
import HolderAnalysisPage from './components/HolderAnalysisPage';
import ProfilePage from './components/ProfilePage';
import CreditsPage from './components/CreditsPage';
import SecurityPage from './components/SecurityPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import PostLoginRedirect from './components/PostLoginRedirect';
import { ScrollToTop } from './components/ScrollToTop';
import MobileWalletPicker from './components/MobileWalletPicker';
import PhantomCallback from './components/PhantomCallback';
import TermsOfServicePage from './components/TermsOfServicePage';
import WhitepaperPage from './components/WhitepaperPage';
import ShillActivityPage from './components/ShillActivityPage';
import TiersPage from './components/TiersPage';
import TrendingPage from './components/TrendingPage';
import IdentityFactorPage from './components/IdentityFactorPage';
import TokenSafetyHistoryPage from './components/TokenSafetyHistoryPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import HolderGraphPage from './components/HolderGraphPage';
import ApiDocsPage from './components/ApiDocsPage';
import AgentsPage from './components/AgentsPage';
import LivingDangerouslyPage from './components/LivingDangerouslyPage';
import TokenCulturePage from './components/TokenCulturePage';
import ConnectTelegramPage from './components/ConnectTelegramPage';
import { initPostHog } from './utils/posthog';

// Import wallet adapter CSS
import '@solana/wallet-adapter-react-ui/styles.css';

// Init PostHog once at module load (before any trackEvent fires).
// No-op without REACT_APP_POSTHOG_KEY, so dev + previews stay quiet.
initPostHog();

function App() {
  // Solana network endpoint (mainnet-beta for production)
  const endpoint = useMemo(() => clusterApiUrl('mainnet-beta'), []);

  // Supported wallets
  // Phantom auto-registers via Wallet Standard on desktop, but needs explicit adapter for mobile deep-linking
  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
    ],
    []
  );

  return (
    <HelmetProvider>
      {/* Floor <Helmet> — sets the document title to the brand
          default. Page-level <Helmet> instances (token detail,
          identity-factor, etc) override this while mounted; when
          they unmount on navigation away, the title falls back here
          instead of leaving the previous page's title in place. */}
      <Helmet defaultTitle="Deepnets.ai">
        <title>Deepnets.ai</title>
      </Helmet>
      <ConnectionProvider endpoint={endpoint}>
        {/* No autoConnect — the wallet adapter would otherwise call
            connect() on the previously-selected wallet on every page
            load, forcing Phantom/Solflare to pop their unlock prompt
            even for users who just want to browse. Components that
            need the wallet (swap, buy credits, sign-in) trigger the
            connection on demand. */}
        <WalletProvider wallets={wallets} autoConnect={false}>
          <WalletModalProvider>
            <AuthProvider>
              <Router>
                <ScrollToTop />
                <PostLoginRedirect />
                <AutoUpdater />
                <MobileWalletPicker />
                <Routes>
                  <Route path="/" element={<MainPage />} />
                  <Route path="/network/:networkAddress" element={<NetworkDetailsPage />} />
                  <Route path="/wallet/:walletAddress" element={<WalletDetailsPage />} />
                  <Route path="/figures" element={<FiguresIndexPage />} />
                  <Route path="/checkout/return" element={<CheckoutReturnPage />} />
                  <Route path="/figures/:key" element={<FigurePage />} />
                  <Route path="/token/:mint" element={<TokenDetailsPage />} />
                  <Route path="/token/:mint/safety-history" element={<TokenSafetyHistoryPage />} />
                  <Route path="/token/:mint/network-graph" element={<HolderGraphPage />} />
                  {/* EVM tokens: /token/<chain>/<0x…>. One static route per chain so an
                      unknown chain segment never shadows the Solana sub-pages above. */}
                  {EVM_CHAIN_IDS.flatMap(c => [
                    <Route key={c} path={`/token/${c}/:mint`} element={<TokenDetailsPage key={c} chain={c} />} />,
                    <Route key={`${c}-holders`} path={`/holder-analysis/${c}/:mint`} element={<HolderAnalysisPage key={c} chain={c} />} />,
                    <Route key={`${c}-holders-ts`} path={`/holder-analysis/${c}/:mint/:timestamp`} element={<HolderAnalysisPage key={c} chain={c} />} />,
                    <Route key={`${c}-graph`} path={`/token/${c}/:mint/network-graph`} element={<HolderGraphPage key={c} chain={c} />} />,
                    <Route key={`${c}-wallet`} path={`/wallet/${c}/:walletAddress`} element={<WalletDetailsPage key={c} chain={c} />} />,
                    <Route key={`${c}-history`} path={`/token/${c}/:mint/safety-history`} element={<TokenSafetyHistoryPage key={c} chain={c} />} />,
                  ])}
                  <Route path="/api-keys" element={<ApiKeysPage />} />
                  {/* Hidden: not linked anywhere (agent lab alpha) */}
                  <Route path="/lab/agents" element={<AgentLabListPage />} />
                  <Route path="/lab/agents/:id" element={<AgentLabAgentPage />} />
                  <Route path="/api-docs" element={<ApiDocsPage />} />
                  <Route path="/agents" element={<AgentsPage />} />
                  {/* Hidden admin tools — not linked from nav. */}
                  <Route path="/token/:mint/culture" element={<TokenCulturePage />} />
                  <Route path="/living-dangerously" element={<LivingDangerouslyPage level="DANGEROUS" title="living_dangerously" />} />
                  <Route path="/living-riskily" element={<LivingDangerouslyPage level="RISKY" title="living_riskily" />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/profile/credits" element={<CreditsPage />} />
                  <Route path="/profile/security" element={<SecurityPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/whitepaper" element={<WhitepaperPage />} />
                  <Route path="/shill-tracker" element={<ShillActivityPage />} />
                  <Route path="/tiers" element={<TiersPage />} />
                  <Route path="/trending" element={<TrendingPage />} />
                  <Route path="/uncfactor" element={<IdentityFactorPage identityKey="unc" />} />
                  {/* Auth verification route (email magic link) — two-step,
                      so prefetchers don't burn the one-time token */}
                  <Route path="/connect/telegram" element={<ConnectTelegramPage />} />
                  <Route path="/auth/verify" element={<VerifyEmailPage />} />
                  <Route path="/auth/phantom-callback" element={<PhantomCallback />} />
                  {/* Backward compatibility - redirect old URLs to latest */}
                  <Route path="/holder-analysis/:mint" element={<HolderAnalysisPage />} />
                  <Route path="/holder-analysis/:mint/:timestamp" element={<HolderAnalysisPage />} />
                </Routes>
              </Router>
            </AuthProvider>
          </WalletModalProvider>
        </WalletProvider>
      </ConnectionProvider>
    </HelmetProvider>
  );
}

export default App;
